import {
	PrintOutlined,
	ReceiptLongOutlined,
	SavingsOutlined,
} from "@mui/icons-material";
import { Grid, Stack } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { useAdvisoryCaseQuery } from "AdvisoryManagement/Api/Queries/useAdvisoryCaseQuery";
import { useInvestmentQuestionnaireQuery } from "AdvisoryManagement/Api/Queries/useInvestmentQuestionnaireQuery";
import { ContractsInProgress } from "AdvisoryManagement/Components/Client/Contracts/ContractsInProgress";
import { ProposedSolutions } from "AdvisoryManagement/Components/Client/ProposedSolutions/ProposedSolutions";
import { PostWaybill } from "AdvisoryManagement/Components/Documents/PostWaybill";
import { InvestmentQuestionnaireButton } from "AdvisoryManagement/Components/InvestmentQuestionnaire/InvestmentQuestionnaireButton";
import { useQuestionnaireStatus } from "AdvisoryManagement/Hooks/useQuestionnaireStatus";
import { postContractsPath } from "Infrastructure/Api/Api";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { ClientInfoCard } from "Shared/Cards/ClientInfoCard";
import { InfoCard } from "Shared/Cards/InfoCard";

export const ClientDetail = () => {
	const { advisoryCaseId } = useParams<{ advisoryCaseId: string }>();
	const [waybillOpen, setWaybillOpen] = useState(false);

	// Get advisory case to extract clientId
	const { data: advisoryCase } = useAdvisoryCaseQuery(advisoryCaseId);
	const clientId = advisoryCase?.clientId;

	// Get investment questionnaire data with status and error message
	const { data: questionnaireResult } =
		useInvestmentQuestionnaireQuery(clientId);

	// Get questionnaire status presentation (color, tooltip, severity)
	const questionnaireStatus = useQuestionnaireStatus(
		questionnaireResult?.status,
		questionnaireResult?.data ?? null,
		questionnaireResult?.errorMessage ?? null,
	);

	const handleWaybillClose = () => {
		setWaybillOpen(false);
	};

	// Map questionnaire severity to InfoCard status
	const getQuestionnaireCardStatus = () => {
		switch (questionnaireStatus.severity) {
			case "success":
				return "completed" as const;
			case "warning":
				return "warning" as const;
			case "error":
				return "error" as const;
			default:
				return "pending" as const;
		}
	};

	// Format date for display
	const formatQuestionnaireDate = () => {
		if (questionnaireResult?.data?.createdAt) {
			return new Date(questionnaireResult.data.createdAt).toLocaleDateString(
				"cs-CZ",
			);
		}
		return "Nevyplněno";
	};

	return (
		<Stack spacing={3} direction="column">
			{/* Client Info Card - Collapsible */}
			<ClientInfoCard
				name="Ing. Jan Chotěborský, Ph.D."
				birthDate="15.03.1975"
				email="jan.choteborsky@email.cz"
				phone="+420 777 123 456"
			/>

			{/* Action Cards Row */}
			<Grid container spacing={2}>
				<Grid size={{ xs: 12, md: 4 }}>
					<InfoCard
						icon={<ReceiptLongOutlined />}
						title="Záznam z jednání"
						subtitle="Poslední aktualizace: 10.01.2026"
						status="completed"
						actionHref={AppRouting.External.Zzj.path()}
						actionLabel="Otevřít"
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 4 }}>
					<InfoCard
						icon={<SavingsOutlined />}
						title="Investiční dotazník"
						subtitle={`Vyplněn: ${formatQuestionnaireDate()}`}
						status={getQuestionnaireCardStatus()}
						statusTooltip={questionnaireStatus.tooltip}
						customActionButton={
							<InvestmentQuestionnaireButton
								clientData={{
									// ToDo: Replace with real client data
									// ToDo: Ask for required namespace
									namespace: "ams",
									clientId: clientId?.toString() || "testId",
									clientEmail: "test@test.test",
									name: "Ing. Jan Chotěborský, Ph.D.",
									password: "10.02.1993",
									entityType: "fo",
									// ToDo: Change to correct url once available
									notificationUrl: postContractsPath(),
								}}
								onQuestionnaireCreated={(data) => {
									console.log("Questionnaire created:", data);
									// TODO: Refetch questionnaire data to update UI
								}}
							/>
						}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 4 }}>
					<InfoCard
						icon={<PrintOutlined />}
						title="Tisk průvodky"
						subtitle="Dokumenty k odeslání: 3"
						status="ready"
						onAction={() => setWaybillOpen(true)}
						actionLabel="Vytvořit"
					/>
				</Grid>
			</Grid>

			{/* Main Content Sections */}
			<ProposedSolutions />
			<ContractsInProgress />

			{/* Modals */}
			{waybillOpen && <PostWaybill handleClose={handleWaybillClose} />}
		</Stack>
	);
};
